import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, updateDoc, query, orderBy, limit, arrayUnion, arrayRemove, deleteDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import Header from '@/components/layout/Header';
import { showSuccess, showError, showConfirm } from '@/lib/swal';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowLeft, Search, Users, Calendar, MapPin, Edit, Save, X, Eye, Building2, Plus, Trash2, UserPlus, LayoutDashboard, Filter, ArrowUpDown, Wallet } from 'lucide-react';

interface UnitData {
  id: string;
  name: string;
  number: string;
  province?: string;
  typeId?: string;
  typeName?: string;
  entityName?: string;
  createdAt: Date;
  createdBy: string;
  members?: any[];
  reviewStatus?: 'pending' | 'approved' | 'denied';
  settings?: {
    timezone?: string;
    currency?: string;
  };
  creditBalance?: number;
}

/** Matches unit settings Members tab (`packages/shared-unit-settings` UserRole). */
type UnitMemberRole = 'Guest' | 'Member' | 'Steward' | 'Treasurer' | 'Secretary';

function normaliseUnitMemberRole(raw: string | undefined): UnitMemberRole {
  if (!raw) return 'Member';
  const r = raw.trim();
  if (['Guest', 'Member', 'Steward', 'Treasurer', 'Secretary'].includes(r)) {
    return r as UnitMemberRole;
  }
  const lower = r.toLowerCase();
  const map: Record<string, UnitMemberRole> = {
    guest: 'Guest',
    member: 'Member',
    steward: 'Steward',
    treasurer: 'Treasurer',
    secretary: 'Secretary',
    officer: 'Member',
    master: 'Member',
    warden: 'Member',
  };
  return map[lower] ?? 'Member';
}

const USER_ROLE_TO_MEMBER_ROLES_FIELD: Record<UnitMemberRole, string> = {
  Guest: 'guest',
  Member: 'member',
  Steward: 'steward',
  Treasurer: 'treasurer',
  Secretary: 'secretary',
};

interface MemberData {
  id: string;
  displayName: string;
  email: string;
  role?: string;
  unitRole?: UnitMemberRole;
}

interface EventData {
  id: string;
  title: string;
  date: Date | null;
  time: string;
  location?: string;
  description?: string;
}

interface CreditTransaction {
  id: string;
  kind: string;
  delta: number;
  balanceAfter: number;
  reason: string;
  createdAt: Date;
  authorisedBy?: string;
}

export default function UnitManagementPage() {
  const navigate = useNavigate();
  const { userProfile, currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState<UnitData[]>([]);
  const [filteredUnits, setFilteredUnits] = useState<UnitData[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('createdAt-desc');
  const [selectedUnit, setSelectedUnit] = useState<UnitData | null>(null);
  const [unitMembers, setUnitMembers] = useState<MemberData[]>([]);
  const [unitEvents, setUnitEvents] = useState<EventData[]>([]);
  const [unitTransactions, setUnitTransactions] = useState<CreditTransaction[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedUnit, setEditedUnit] = useState<UnitData | null>(null);
  const [saving, setSaving] = useState(false);
  const [unitTypes, setUnitTypes] = useState<{ id: string; name: string; entityName: string }[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [allUsers, setAllUsers] = useState<MemberData[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserRole, setSelectedUserRole] = useState('member');
  const [addingMember, setAddingMember] = useState(false);
  const [updatingMemberRole, setUpdatingMemberRole] = useState<string | null>(null);

  const [adjustingCredits, setAdjustingCredits] = useState(false);
  const [creditAdjustmentDelta, setCreditAdjustmentDelta] = useState('');
  const [creditAdjustmentReason, setCreditAdjustmentReason] = useState('');

  useEffect(() => {
    fetchUnits();
    fetchUnitTypes();
    fetchAllUsers();
  }, []);

  useEffect(() => {
    // Filter and sort units
    let filtered = [...units];

    // Apply search filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (unit) =>
          unit.name.toLowerCase().includes(query) ||
          unit.number.toLowerCase().includes(query) ||
          unit.province?.toLowerCase().includes(query) ||
          unit.typeName?.toLowerCase().includes(query)
      );
    }

    // Apply review status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((unit) => unit.reviewStatus === statusFilter);
    }

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter((unit) => unit.typeId === typeFilter);
    }

    // Apply sorting
    const [sortField, sortDirection] = sortBy.split('-');
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'number':
          aValue = a.number.toLowerCase();
          bValue = b.number.toLowerCase();
          break;
        case 'type':
          aValue = a.typeName || '';
          bValue = b.typeName || '';
          break;
        case 'status':
          aValue = a.reviewStatus || '';
          bValue = b.reviewStatus || '';
          break;
        case 'createdAt':
        default:
          aValue = a.createdAt?.getTime() || 0;
          bValue = b.createdAt?.getTime() || 0;
          break;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredUnits(filtered);
  }, [searchQuery, units, statusFilter, typeFilter, sortBy]);

  const fetchUnitTypes = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        if (data.unitTypes) {
          setUnitTypes(data.unitTypes);
        }
      }
    } catch (error) {
      console.error('Error fetching unit types:', error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersList: MemberData[] = [];
      
      if (!usersSnapshot.empty) {
        usersSnapshot.forEach((userDoc) => {
          const data = userDoc.data();
          // Include all users, even those without email or displayName
          usersList.push({
            id: userDoc.id,
            displayName: data.displayName || data.email || `User ${userDoc.id.substring(0, 8)}`,
            email: data.email || 'No email',
            role: data.role || (data.isAdmin ? 'admin' : 'user'),
          });
        });
      }
      
      // Sort users alphabetically by displayName for easier searching
      usersList.sort((a, b) => a.displayName.localeCompare(b.displayName));
      
      console.log(`Fetched ${usersList.length} total users from Firestore`);
      setAllUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
      showError('Failed to load users. Please check your permissions.');
      // Set empty array on error so the UI still works
      setAllUsers([]);
    }
  };

  const fetchUnits = async () => {
    setLoading(true);
    try {
      // Fetch all units without limit
      const unitsQuery = query(
        collection(db, 'units'),
        orderBy('createdAt', 'desc')
      );
      const unitsSnapshot = await getDocs(unitsQuery);
      const unitsList: UnitData[] = [];

      // Fetch unit types once for efficiency
      const typeDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
      const unitTypesMap = new Map<string, { name: string; entityName: string }>();
      if (typeDoc.exists()) {
        const settings = typeDoc.data();
        if (settings.unitTypes) {
          settings.unitTypes.forEach((t: any) => {
            unitTypesMap.set(t.id, { name: t.name, entityName: t.entityName });
          });
        }
      }

      for (const unitDoc of unitsSnapshot.docs) {
        const data = unitDoc.data();
        
        const unit: UnitData = {
          id: unitDoc.id,
          name: data.name || '',
          number: data.unitNumber || data.number || '',
          province: data.province,
          typeId: data.typeId,
          createdAt: data.createdAt?.toDate() || new Date(),
          createdBy: data.createdBy || '',
          members: data.members || [],
          reviewStatus: data.reviewStatus || 'approved',
          settings: data.settings || {},
          creditBalance: data.creditBalance ?? 0,
        };

        // Get type name from map
        if (unit.typeId) {
          const unitType = unitTypesMap.get(unit.typeId);
          if (unitType) {
            unit.typeName = unitType.name;
            unit.entityName = unitType.entityName;
          }
        }

        unitsList.push(unit);
      }

      console.log(`✅ Fetched ${unitsList.length} units`);
      setUnits(unitsList);
      setFilteredUnits(unitsList);
    } catch (error) {
      console.error('Error fetching units:', error);
      showError('Failed to load units');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnitDetails = async (unitId: string) => {
    try {
      setLoading(true);

      // Fetch unit document to get members array and their roles
      const unitDoc = await getDoc(doc(db, 'units', unitId));
      const unitData = unitDoc.data();
      const unitMembersRaw = unitData?.members || [];
      const unitMemberIds = unitMembersRaw.map((m: any) => typeof m === 'string' ? m : m.uid).filter(Boolean);
      const memberRoles = unitData?.memberRoles || {};

      // Fetch members
      const membersSnapshot = await getDocs(collection(db, 'users'));
      const membersList: MemberData[] = [];
      
      membersSnapshot.forEach((memberDoc) => {
        const data = memberDoc.data();
        if (data.unitId === unitId || unitMemberIds.includes(memberDoc.id)) {
          const memberEntry = unitMembersRaw.find(
            (m: any) => (typeof m === 'string' ? m : m.uid) === memberDoc.id
          );
          const rawRole =
            typeof memberEntry === 'object' &&
            memberEntry != null &&
            (memberEntry as { role?: string }).role != null
              ? String((memberEntry as { role: string }).role)
              : memberRoles[memberDoc.id];
          membersList.push({
            id: memberDoc.id,
            displayName: data.displayName || data.email || 'Unknown',
            email: data.email || '',
            role: data.role || 'user',
            unitRole: normaliseUnitMemberRole(rawRole),
          });
        }
      });
      setUnitMembers(membersList);

      // Fetch events
      const eventsSnapshot = await getDocs(
        query(
          collection(db, 'units', unitId, 'events'),
          orderBy('date', 'desc'),
          limit(20)
        )
      );
      const eventsList: EventData[] = [];
      
      eventsSnapshot.forEach((eventDoc) => {
        const data = eventDoc.data();
        // Handle Firestore Timestamp conversion
        let eventDate: Date | null = null;
        if (data.date) {
          if (data.date.toDate && typeof data.date.toDate === 'function') {
            // Firestore Timestamp
            eventDate = data.date.toDate();
          } else if (data.date instanceof Date) {
            // Already a Date object
            eventDate = data.date;
          } else if (typeof data.date === 'string') {
            // String date
            const parsedDate = new Date(data.date);
            eventDate = isNaN(parsedDate.getTime()) ? null : parsedDate;
          }
        }
        
        // Format time from the Date object (HH:MM format)
        const timeStr = eventDate ? eventDate.toTimeString().slice(0, 5) : '';
        
        eventsList.push({
          id: eventDoc.id,
          title: data.name || data.title || '',
          date: eventDate,
          time: timeStr,
          location: data.location,
          description: data.description,
        });
      });
      setUnitEvents(eventsList);

      // Fetch credit transactions
      const txSnapshot = await getDocs(
        query(
          collection(db, 'units', unitId, 'creditTransactions'),
          orderBy('createdAt', 'desc'),
          limit(50)
        )
      );
      const txList: CreditTransaction[] = [];
      txSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        txList.push({
          id: docSnap.id,
          kind: data.kind || 'unknown',
          delta: data.delta || 0,
          balanceAfter: data.balanceAfter || 0,
          reason: data.reason || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          authorisedBy: data.metadata?.authorisedBy,
        });
      });
      setUnitTransactions(txList);
    } catch (error) {
      console.error('Error fetching unit details:', error);
      showError('Failed to load unit details');
    } finally {
      setLoading(false);
    }
  };

  const handleViewUnit = async (unit: UnitData) => {
    setSelectedUnit(unit);
    setIsEditing(false);
    await fetchUnitDetails(unit.id);
  };

  const handleEditUnit = () => {
    if (selectedUnit) {
      setEditedUnit({ ...selectedUnit });
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedUnit(null);
  };

  const handleSaveUnit = async () => {
    if (!editedUnit || !selectedUnit) return;

    setSaving(true);
    try {
      const unitRef = doc(db, 'units', selectedUnit.id);
      await updateDoc(unitRef, {
        name: editedUnit.name,
        number: editedUnit.number,
        province: editedUnit.province,
        typeId: editedUnit.typeId,
        settings: editedUnit.settings,
      });

      // Update local state
      const updatedUnits = units.map((u) =>
        u.id === selectedUnit.id ? editedUnit : u
      );
      setUnits(updatedUnits);
      setSelectedUnit(editedUnit);
      setIsEditing(false);
      setEditedUnit(null);

      showSuccess('Unit updated successfully!');
    } catch (error) {
      console.error('Error updating unit:', error);
      showError('Failed to update unit');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUnit = async () => {
    if (!selectedUnit) return;

    const confirmed = await showConfirm(
      'Delete Unit',
      'Are you sure you want to delete this unit? This action cannot be undone.',
      'warning'
    );

    if (!confirmed) {
      return;
    }

    setDeleting(true);

    try {
      const unitId = selectedUnit.id;
      const unitRef = doc(db, 'units', unitId);

      // Clean up unit memberships from user documents
      const memberIds =
        (selectedUnit.members && selectedUnit.members.length > 0
          ? selectedUnit.members.map((m: any) => typeof m === 'string' ? m : m.uid).filter(Boolean)
          : unitMembers.map((member) => member.id)) || [];

      for (const memberId of memberIds) {
        const userRef = doc(db, 'users', memberId);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();
        const existingUnits = userData?.units || [];

        const membershipToRemove = existingUnits.find(
          (membership: any) => membership?.unitId === unitId || membership?.lodgeId === unitId
        );

        if (membershipToRemove) {
          await updateDoc(userRef, {
            units: arrayRemove(membershipToRemove),
          });
        }
      }

      await deleteDoc(unitRef);

      setUnits(units.filter((unit) => unit.id !== unitId));
      setFilteredUnits(filteredUnits.filter((unit) => unit.id !== unitId));

      showSuccess('Unit deleted successfully!');
      handleBackToList();
    } catch (error) {
      console.error('Error deleting unit:', error);
      showError('Failed to delete unit');
    } finally {
      setDeleting(false);
    }
  };

  const handleBackToList = () => {
    setSelectedUnit(null);
    setUnitMembers([]);
    setUnitEvents([]);
    setUnitTransactions([]);
    setIsEditing(false);
    setEditedUnit(null);
    setShowAddMember(false);
    setUserSearchQuery('');
    setSelectedUserId('');
    setSelectedUserRole('member');
    setCreditAdjustmentDelta('');
    setCreditAdjustmentReason('');
    setUpdatingMemberRole(null);
  };

  const handleAddMemberToUnit = async () => {
    if (!selectedUnit || !selectedUserId) {
      showError('Please select a user to add');
      return;
    }

    // Check if user is already a member
    const isAlreadyMember = selectedUnit.members?.some(m => 
      typeof m === 'string' ? m === selectedUserId : m.uid === selectedUserId
    );
    if (isAlreadyMember) {
      showError('This user is already a member of this unit');
      return;
    }

    setAddingMember(true);
    try {
      const unitRef = doc(db, 'units', selectedUnit.id);
      const userRef = doc(db, 'users', selectedUserId);

      // Get user document to check existing memberships
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      const existingUnits = userData?.units || [];

      // Check if user already has this unit in their units array
      const alreadyMember = existingUnits.some(
        (membership: any) => membership?.unitId === selectedUnit.id || membership?.lodgeId === selectedUnit.id
      );

      if (alreadyMember) {
        showError('This user is already a member of this unit');
        setAddingMember(false);
        return;
      }

      // Map role to UserRole type (capitalize first letter)
      const roleMap: Record<string, string> = {
        member: 'Member',
        secretary: 'Secretary',
        treasurer: 'Treasurer',
        steward: 'Steward',
        officer: 'Member',
        master: 'Member',
        guest: 'Guest',
      };
      const mappedRole = roleMap[selectedUserRole.toLowerCase()] || 'Member';

      // Get current members array
      const currentMembers = selectedUnit.members || [];
      const newMemberObj = {
        uid: selectedUserId,
        role: mappedRole,
        joinedAt: new Date()
      };

      // Update unit document
      await updateDoc(unitRef, {
        members: [...currentMembers, newMemberObj],
        [`memberRoles.${selectedUserId}`]: selectedUserRole,
      });

      // Update user document - add to units array
      const newMembership = {
        unitId: selectedUnit.id,
        role: mappedRole,
        joinedAt: new Date(),
      };

      await updateDoc(userRef, {
        units: arrayUnion(newMembership),
      });

      // Update local state
      const addedUser = allUsers.find(u => u.id === selectedUserId);
      if (addedUser) {
        setUnitMembers([
          ...unitMembers,
          { ...addedUser, unitRole: normaliseUnitMemberRole(mappedRole) },
        ]);
        setSelectedUnit({
          ...selectedUnit,
          members: [...(selectedUnit.members || []), newMemberObj],
        });
      }

      // Update units list
      const updatedUnits = units.map((u) =>
        u.id === selectedUnit.id
          ? { ...u, members: [...(u.members || []), newMemberObj] }
          : u
      );
      setUnits(updatedUnits);

      showSuccess('Member added successfully!');
      setShowAddMember(false);
      setUserSearchQuery('');
      setSelectedUserId('');
      setSelectedUserRole('member');
    } catch (error) {
      console.error('Error adding member:', error);
      showError('Failed to add member to unit');
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMemberFromUnit = async (memberId: string) => {
    if (!selectedUnit) return;

    const confirmed = await showConfirm(
      'Remove Member',
      'Are you sure you want to remove this member from the unit?',
      'warning'
    );

    if (!confirmed) return;

    try {
      const unitRef = doc(db, 'units', selectedUnit.id);
      const userRef = doc(db, 'users', memberId);

      // Get user document to find the specific membership to remove
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      const existingUnits = userData?.units || [];

      // Find the membership object for this unit
      const membershipToRemove = existingUnits.find(
        (membership: any) => membership?.unitId === selectedUnit.id || membership?.lodgeId === selectedUnit.id
      );
      
      const unitDoc = await getDoc(unitRef);
      const currentMembers = unitDoc.data()?.members || [];
      const updatedMembers = currentMembers.filter((m: any) => (typeof m === 'string' ? m : m.uid) !== memberId);

      // Update unit document
      await updateDoc(unitRef, {
        members: updatedMembers,
        [`memberRoles.${memberId}`]: null,
      });

      // Update user document - remove from units array
      if (membershipToRemove) {
        await updateDoc(userRef, {
          units: arrayRemove(membershipToRemove),
        });
      }

      // Update local state
      setUnitMembers(unitMembers.filter(m => m.id !== memberId));
      setSelectedUnit({
        ...selectedUnit,
        members: (selectedUnit.members || []).filter((m: any) => (typeof m === 'string' ? m : m.uid) !== memberId),
      });

      // Update units list
      const updatedUnits = units.map((u) =>
        u.id === selectedUnit.id
          ? { ...u, members: (u.members || []).filter((m: any) => (typeof m === 'string' ? m : m.uid) !== memberId) }
          : u
      );
      setUnits(updatedUnits);

      showSuccess('Member removed successfully!');
    } catch (error) {
      console.error('Error removing member:', error);
      showError('Failed to remove member from unit');
    }
  };

  const handleUpdateMemberRole = async (memberId: string, newRole: UnitMemberRole) => {
    if (!selectedUnit) return;
    if (memberId.startsWith('email:')) {
      showError(
        'Cannot change role for members without accounts. Update the unit in the app or remove and re-import after they register.'
      );
      return;
    }

    setUpdatingMemberRole(memberId);
    try {
      const unitRef = doc(db, 'units', selectedUnit.id);
      const userRef = doc(db, 'users', memberId);

      const currentMembers = selectedUnit.members || [];
      const updatedMembers = currentMembers.map((m: any) => {
        const uid = typeof m === 'string' ? m : m.uid;
        if (uid !== memberId) return m;
        if (typeof m === 'string') {
          return { uid: m, role: newRole, joinedAt: new Date() };
        }
        return { ...m, role: newRole };
      });

      const roleFieldValue = USER_ROLE_TO_MEMBER_ROLES_FIELD[newRole];

      await updateDoc(unitRef, {
        members: updatedMembers,
        [`memberRoles.${memberId}`]: roleFieldValue,
      });

      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userUnits = userData.units || [];
        const updatedUserUnits = userUnits.map((l: any) => {
          const id = l.unitId || l.lodgeId;
          return id === selectedUnit.id ? { ...l, role: newRole } : l;
        });
        await updateDoc(userRef, {
          units: updatedUserUnits,
        });
      }

      setUnitMembers(
        unitMembers.map((row) =>
          row.id === memberId ? { ...row, unitRole: newRole } : row
        )
      );
      setSelectedUnit({
        ...selectedUnit,
        members: updatedMembers,
      });
      setUnits(
        units.map((u) =>
          u.id === selectedUnit.id ? { ...u, members: updatedMembers } : u
        )
      );

      showSuccess('Member role updated successfully');
    } catch (error) {
      console.error('Error updating member role:', error);
      showError('Failed to update member role');
    } finally {
      setUpdatingMemberRole(null);
    }
  };

  const handleAdjustCredits = async () => {
    if (!selectedUnit) return;
    const delta = parseInt(creditAdjustmentDelta, 10);
    if (isNaN(delta) || delta === 0) {
      showError('Please enter a valid non-zero amount');
      return;
    }
    if (!creditAdjustmentReason) {
      showError('Please enter a reason');
      return;
    }

    setAdjustingCredits(true);
    try {
      const fn = httpsCallable(functions, 'adminAdjustUnitCredits');
      const res = await fn({ unitId: selectedUnit.id, delta, reason: creditAdjustmentReason });
      const newBalance = (res.data as any)?.newBalance;
      
      const updatedUnit = { ...selectedUnit, creditBalance: newBalance };
      setSelectedUnit(updatedUnit);
      setUnits(units.map(u => u.id === selectedUnit.id ? updatedUnit : u));
      
      showSuccess(`Credits adjusted successfully! New balance: ${newBalance}`);
      setCreditAdjustmentDelta('');
      setCreditAdjustmentReason('');
      fetchUnitDetails(selectedUnit.id);
    } catch (e) {
      console.error(e);
      showError('Failed to adjust credits');
    } finally {
      setAdjustingCredits(false);
    }
  };

  const getAvailableUsers = () => {
    const currentMemberIds = (selectedUnit?.members || []).map((m: any) => typeof m === 'string' ? m : m.uid).filter(Boolean);
    const available = allUsers.filter(user => !currentMemberIds.includes(user.id));
    console.log(`Available users (not in unit): ${available.length} of ${allUsers.length} total users`);
    return available;
  };

  const getFilteredAvailableUsers = () => {
    const available = getAvailableUsers();
    if (!userSearchQuery.trim()) return available;
    
    const query = userSearchQuery.toLowerCase();
    const filtered = available.filter(
      user =>
        user.displayName.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        user.role?.toLowerCase().includes(query)
    );
    console.log(`Filtered users for query "${userSearchQuery}": ${filtered.length} matches`);
    return filtered;
  };

  const canViewDashboard = (unit: UnitData): boolean => {
    if (!userProfile || !currentUser) return false;
    
    // Admin can view any unit dashboard
    if (userProfile.isAdmin) return true;
    
    // Check if user belongs to this unit
    const userUnits = userProfile.units || [];
    return userUnits.some((membership: any) => {
      const unitId = membership.unitId || membership.lodgeId;
      return unitId === unit.id;
    });
  };

  const handleViewDashboard = (unit: UnitData) => {
    const diningUrl = import.meta.env.VITE_DINING_URL || 'http://localhost:3000';
    window.location.href = `${diningUrl}/unit/${unit.id}/dashboard`;
  };

  const handleViewEventDashboard = (unit: UnitData, eventId: string) => {
    const diningUrl = import.meta.env.VITE_DINING_URL || 'http://localhost:3000';
    window.location.href = `${diningUrl}/unit/${unit.id}/event/${eventId}`;
  };

  const getStatusBadgeVariant = (status?: string) => {
    switch (status) {
      case 'approved':
      case 'active':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'denied':
      case 'cancelled':
        return 'destructive';
      case 'past_due':
        return 'destructive';
      case 'trial':
        return 'outline';
      default:
        return 'outline';
    }
  };

  if (loading && !selectedUnit) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading units...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-6xl">
          {!selectedUnit ? (
            <>
              {/* List View */}
              <div className="mb-8">
                <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Dashboard
                </Button>
                <h1 className="text-3xl font-bold text-foreground">Unit Management</h1>
                <p className="text-muted-foreground">View and manage all units on the platform</p>
              </div>

              {/* Search and Filters */}
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    {/* Search */}
                    <div className="flex items-center gap-2">
                      <Search className="h-5 w-5 text-muted-foreground" />
                      <Input
                        placeholder="Search by name, number, province, or type..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1"
                      />
                    </div>

                    {/* Filters */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm flex items-center gap-2">
                          <Filter className="h-4 w-4" />
                          Review Status
                        </Label>
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <option value="all">All Statuses</option>
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="denied">Denied</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm">Unit Type</Label>
                        <select
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value)}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <option value="all">All Types</option>
                          {unitTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm flex items-center gap-2">
                          <ArrowUpDown className="h-4 w-4" />
                          Sort By
                        </Label>
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <option value="createdAt-desc">Newest First</option>
                          <option value="createdAt-asc">Oldest First</option>
                          <option value="name-asc">Name (A-Z)</option>
                          <option value="name-desc">Name (Z-A)</option>
                          <option value="number-asc">Number (Ascending)</option>
                          <option value="number-desc">Number (Descending)</option>
                          <option value="type-asc">Type (A-Z)</option>
                          <option value="status-asc">Status</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Units List */}
              <Card>
                <CardHeader>
                  <CardTitle>All Units</CardTitle>
                  <CardDescription>
                    Showing {filteredUnits.length} of {units.length} units
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredUnits.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p className="text-sm font-medium">No units found</p>
                      <p className="text-xs mt-1">
                        {searchQuery ? 'Try a different search term' : 'No units have been created yet'}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="min-w-[200px]">Unit</TableHead>
                            <TableHead className="min-w-[140px]">Type</TableHead>
                            <TableHead className="w-[110px]">Review</TableHead>
                            <TableHead className="min-w-[120px]">Province</TableHead>
                            <TableHead className="text-right w-[90px]">Members</TableHead>
                            <TableHead className="text-right w-[110px]">
                              <span className="inline-flex items-center gap-1 justify-end">
                                <Wallet className="h-4 w-4" aria-hidden />
                                Credits
                              </span>
                            </TableHead>
                            <TableHead className="w-[110px] whitespace-nowrap">Created</TableHead>
                            <TableHead className="w-[260px] text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredUnits.map((unit) => {
                            const balance = unit.creditBalance ?? 0;
                            return (
                              <TableRow key={unit.id} className="hover:bg-muted/50">
                                <TableCell className="font-medium">
                                  {unit.name} No. {unit.number}
                                </TableCell>
                                <TableCell>
                                  {unit.typeName ? (
                                    <Badge variant="outline" className="font-normal">
                                      {unit.typeName}
                                      {unit.entityName ? ` (${unit.entityName})` : ''}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted-foreground text-sm">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {unit.reviewStatus ? (
                                    <Badge variant={getStatusBadgeVariant(unit.reviewStatus)}>
                                      {unit.reviewStatus.charAt(0).toUpperCase() +
                                        unit.reviewStatus.slice(1)}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted-foreground text-sm">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm">
                                  {unit.province ? (
                                    <span className="inline-flex items-center gap-1">
                                      <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                      {unit.province}
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                  {unit.members?.length ?? 0}
                                </TableCell>
                                <TableCell
                                  className={`text-right tabular-nums font-medium ${
                                    balance < 0 ? 'text-destructive' : ''
                                  }`}
                                >
                                  {balance}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                                  {unit.createdAt.toLocaleDateString()}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex flex-wrap justify-end gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleViewUnit(unit)}
                                    >
                                      <Eye className="mr-2 h-4 w-4" />
                                      Details
                                    </Button>
                                    {canViewDashboard(unit) && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleViewDashboard(unit)}
                                      >
                                        <LayoutDashboard className="mr-2 h-4 w-4" />
                                        Dashboard
                                      </Button>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* Detail View */}
              <div className="mb-8">
                <Button variant="ghost" onClick={handleBackToList} className="mb-4">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Units List
                </Button>
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-foreground">
                      {selectedUnit.name} No. {selectedUnit.number}
                    </h1>
                    <p className="text-muted-foreground">Unit Details and Management</p>
                  </div>
                  {!isEditing ? (
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={handleEditUnit}>
                        <Edit className="mr-2 h-4 w-4" />
                        Edit Unit
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleDeleteUnit}
                        disabled={deleting}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {deleting ? 'Deleting...' : 'Delete Unit'}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={handleCancelEdit}>
                        <X className="mr-2 h-4 w-4" />
                        Cancel
                      </Button>
                      <Button onClick={handleSaveUnit} disabled={saving}>
                        <Save className="mr-2 h-4 w-4" />
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                {/* Unit Information */}
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5" />
                      Unit Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {isEditing && editedUnit ? (
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Unit Name</Label>
                          <Input
                            value={editedUnit.name}
                            onChange={(e) =>
                              setEditedUnit({ ...editedUnit, name: e.target.value })
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Unit Number</Label>
                          <Input
                            value={editedUnit.number}
                            onChange={(e) =>
                              setEditedUnit({ ...editedUnit, number: e.target.value })
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Province</Label>
                          <Input
                            value={editedUnit.province || ''}
                            onChange={(e) =>
                              setEditedUnit({ ...editedUnit, province: e.target.value })
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Unit Type</Label>
                          <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            value={editedUnit.typeId || ''}
                            onChange={(e) =>
                              setEditedUnit({ ...editedUnit, typeId: e.target.value })
                            }
                          >
                            <option value="">Select a type</option>
                            {unitTypes.map((type) => (
                              <option key={type.id} value={type.id}>
                                {type.name} ({type.entityName})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label>Timezone</Label>
                          <Input
                            value={editedUnit.settings?.timezone || ''}
                            onChange={(e) =>
                              setEditedUnit({
                                ...editedUnit,
                                settings: { ...editedUnit.settings, timezone: e.target.value },
                              })
                            }
                            placeholder="e.g., Europe/London"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Currency</Label>
                          <Input
                            value={editedUnit.settings?.currency || ''}
                            onChange={(e) =>
                              setEditedUnit({
                                ...editedUnit,
                                settings: { ...editedUnit.settings, currency: e.target.value },
                              })
                            }
                            placeholder="e.g., GBP"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <Label className="text-muted-foreground">Unit Name</Label>
                          <p className="font-medium">{selectedUnit.name}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Unit Number</Label>
                          <p className="font-medium">{selectedUnit.number}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Province</Label>
                          <p className="font-medium">{selectedUnit.province || 'Not specified'}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Unit Type</Label>
                          <p className="font-medium">
                            {selectedUnit.typeName || 'Not specified'}
                            {selectedUnit.entityName && ` (${selectedUnit.entityName})`}
                          </p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Timezone</Label>
                          <p className="font-medium">
                            {selectedUnit.settings?.timezone || 'Not specified'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Currency</Label>
                          <p className="font-medium">
                            {selectedUnit.settings?.currency || 'Not specified'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Created At</Label>
                          <p className="font-medium">
                            {selectedUnit.createdAt.toLocaleDateString()} at{' '}
                            {selectedUnit.createdAt.toLocaleTimeString()}
                          </p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Created By</Label>
                          <p className="font-medium">{selectedUnit.createdBy}</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Credit Management */}
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Wallet className="h-5 w-5" />
                      Credit Management
                    </CardTitle>
                    <div className="text-sm text-muted-foreground mt-1.5 flex items-center">
                      Current Balance: <Badge variant={selectedUnit.creditBalance !== undefined && selectedUnit.creditBalance < 0 ? 'destructive' : 'secondary'} className="ml-2">{selectedUnit.creditBalance ?? 0}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Adjustment Amount</Label>
                          <Input
                            type="number"
                            placeholder="e.g. 50 or -50"
                            value={creditAdjustmentDelta}
                            onChange={(e) => setCreditAdjustmentDelta(e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">Use positive numbers to grant, negative to remove.</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Reason</Label>
                          <Input
                            placeholder="Reason for adjustment"
                            value={creditAdjustmentReason}
                            onChange={(e) => setCreditAdjustmentReason(e.target.value)}
                          />
                        </div>
                      </div>
                      <Button 
                        onClick={handleAdjustCredits} 
                        disabled={adjustingCredits || !creditAdjustmentDelta || !creditAdjustmentReason}
                      >
                        {adjustingCredits ? 'Adjusting...' : 'Adjust Credits'}
                      </Button>

                      {unitTransactions.length > 0 && (
                        <div className="mt-8 border-t pt-4">
                          <h4 className="text-sm font-medium mb-3">Transaction History</h4>
                          <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                            {unitTransactions.map(tx => (
                              <div key={tx.id} className="flex justify-between items-center p-3 text-sm border rounded-md">
                                <div>
                                  <div className="font-medium">{tx.reason || tx.kind}</div>
                                  <div className="text-xs text-muted-foreground">{tx.createdAt.toLocaleString()}</div>
                                </div>
                                <div className="text-right">
                                  <div className={`font-semibold ${tx.delta > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {tx.delta > 0 ? '+' : ''}{tx.delta}
                                  </div>
                                  <div className="text-xs text-muted-foreground">Balance: {tx.balanceAfter}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Members */}
                <Card className="md:col-span-2">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Members ({unitMembers.length})
                      </CardTitle>
                      {!isEditing && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowAddMember(!showAddMember)}
                        >
                          {showAddMember ? (
                            <>
                              <X className="mr-2 h-4 w-4" />
                              Cancel
                            </>
                          ) : (
                            <>
                              <UserPlus className="mr-2 h-4 w-4" />
                              Add Member
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {showAddMember && (
                      <div className="mb-4 p-4 border rounded-lg bg-muted/50">
                        <h4 className="font-medium mb-3">Add New Member</h4>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-sm">Search Users</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Search className="h-4 w-4 text-muted-foreground" />
                              <Input
                                placeholder="Search by name or email..."
                                value={userSearchQuery}
                                onChange={(e) => setUserSearchQuery(e.target.value)}
                                className="flex-1"
                              />
                            </div>
                          </div>

                          <div>
                            <Label className="text-sm">Select User</Label>
                            <select
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring mt-1"
                              value={selectedUserId}
                              onChange={(e) => setSelectedUserId(e.target.value)}
                            >
                              <option value="">-- Select a user --</option>
                              {getFilteredAvailableUsers().map((user) => (
                                <option key={user.id} value={user.id}>
                                  {user.displayName} ({user.email}) {user.role === 'admin' ? '[ADMIN]' : ''}
                                </option>
                              ))}
                            </select>
                            {getFilteredAvailableUsers().length === 0 && userSearchQuery && (
                              <p className="text-xs text-muted-foreground mt-1">
                                No users found matching "{userSearchQuery}"
                              </p>
                            )}
                            {!userSearchQuery && getFilteredAvailableUsers().length === 0 && (
                              <p className="text-xs text-muted-foreground mt-1">
                                All users are already members of this unit
                              </p>
                            )}
                          </div>

                          <div>
                            <Label className="text-sm">Unit Role</Label>
                            <select
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring mt-1"
                              value={selectedUserRole}
                              onChange={(e) => setSelectedUserRole(e.target.value)}
                            >
                              <option value="member">Member</option>
                              <option value="officer">Officer</option>
                              <option value="secretary">Secretary</option>
                              <option value="treasurer">Treasurer</option>
                              <option value="master">Worshipful Master</option>
                              <option value="warden">Warden</option>
                              <option value="steward">Steward</option>
                            </select>
                          </div>

                          <Button
                            onClick={handleAddMemberToUnit}
                            disabled={!selectedUserId || addingMember}
                            className="w-full"
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            {addingMember ? 'Adding...' : 'Add Member'}
                          </Button>
                        </div>
                      </div>
                    )}

                    {unitMembers.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No members found</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {unitMembers.map((member) => (
                          <div
                            key={member.id}
                            className="flex items-center justify-between p-3 rounded-lg border"
                          >
                            <div className="flex-1">
                              <p className="font-medium">{member.displayName}</p>
                              <p className="text-sm text-muted-foreground">{member.email}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {!isEditing ? (
                                <Select
                                  value={member.unitRole ?? 'Member'}
                                  onValueChange={(value) =>
                                    handleUpdateMemberRole(member.id, value as UnitMemberRole)
                                  }
                                  disabled={updatingMemberRole === member.id}
                                >
                                  <SelectTrigger className="w-[160px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Guest">Guest</SelectItem>
                                    <SelectItem value="Member">Member</SelectItem>
                                    <SelectItem value="Steward">Steward</SelectItem>
                                    <SelectItem value="Treasurer">Treasurer</SelectItem>
                                    <SelectItem value="Secretary">Secretary</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Badge variant="outline">{member.unitRole ?? 'Member'}</Badge>
                              )}
                              {!isEditing && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={updatingMemberRole === member.id}
                                  onClick={() => handleRemoveMemberFromUnit(member.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Events */}
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Events ({unitEvents.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {unitEvents.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No events found</p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {unitEvents.map((event) => (
                          <div
                            key={event.id}
                            className="p-3 rounded-lg border flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                          >
                            <div>
                              <h4 className="font-medium mb-1">{event.title}</h4>
                              <div className="text-sm text-muted-foreground space-y-1">
                                <p>
                                  {event.date && !isNaN(event.date.getTime()) 
                                    ? `${event.date.toLocaleDateString()}${event.time ? ` at ${event.time}` : ''}`
                                    : 'Date not available'}
                                </p>
                                {event.location && (
                                  <p className="flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    {event.location}
                                  </p>
                                )}
                                {event.description && (
                                  <p className="text-xs mt-2">{event.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              {canViewDashboard(selectedUnit) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewEventDashboard(selectedUnit, event.id)}
                                >
                                  <LayoutDashboard className="mr-2 h-4 w-4" />
                                  Event Dashboard
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

